import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { REACT_APP_API_URL } from '../config';
import { apiFetch } from '../App';
import { getUserEmail } from '../utils/userUtils';
import './Home.css';

function Home({ onLogin }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [models, setModels] = useState([]);
  const [createMode, setCreateMode] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [nameExists, setNameExists] = useState(false);
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [newExperiment, setNewExperiment] = useState({
    name: '',
    model_1: '',
    model_2: ''
  });

  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('accessToken');

  useEffect(() => {
    if (isLoggedIn) {
      fetchExperiments();
      fetchModels();
    } else {
      setLoading(false);
    }
  }, [isLoggedIn]);

  const fetchExperiments = async () => {
    try {
      const response = await apiFetch('experiments');
      if (response.ok) {
        const data = await response.json();
        setExperiments(data);
      } else {
        setError('Failed to load experiments');
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await apiFetch('models');
      if (response.ok) {
        const data = await response.json();
        setModels(data);
      } else {
        setError('Failed to load models');
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const checkExperimentName = async (name) => {
    if (!name) {
      setNameExists(false);
      return;
    }
    
    try {
      setNameCheckLoading(true);
      const response = await apiFetch(`experiments/check-name/${encodeURIComponent(name)}`);
      if (response.ok) {
        const data = await response.json();
        setNameExists(data.exists);
      }
    } catch (error) {
      console.error('Error checking experiment name:', error);
    } finally {
      setNameCheckLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewExperiment(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'name') {
      checkExperimentName(value);
    }
  };

  const handleCreateExperiment = async (e) => {
    e.preventDefault();
    
    if (!newExperiment.name || !newExperiment.model_1 || !newExperiment.model_2) {
      setError('Please fill in all fields');
      return;
    }
    
    if (nameExists) {
      setErrorMessage("An experiment with this name already exists. Please choose a different name.");
      setShowErrorModal(true);
      return;
    }
    
    try {
      setLoading(true);
      
      const userData = await getUserData();
      
      const experimentData = {
        ...newExperiment,
        user: userData.email
      };
      
      const response = await apiFetch('experiments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(experimentData)
      });
      
      if (response.ok) {
        // Reset form and fetch updated experiments
        setNewExperiment({
          name: '',
          model_1: '',
          model_2: ''
        });
        setNameExists(false);
        setCreateMode(false);
        await fetchExperiments();
      } else {
        const errorText = await response.text();
        if (response.status === 422) {
          setErrorMessage("No compatible images were found, please select a compatible pair of models");
          setShowErrorModal(true);
        } else {
          setError(`Failed to create experiment: ${errorText}`);
        }
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getUserData = async () => {
    try {
      return {
        email: await getUserEmail()
      };
    } catch (error) {
      console.error('Error getting user data:', error);
      throw new Error('Could not get user data');
    }
  };

  const handleStartExperiment = (experimentId) => {
    navigate(`/compare/${experimentId}`);
  };

  const handleViewResults = (experimentId) => {
    navigate(`/results/${experimentId}`);
  };

  const handleDeleteExperiment = async (experimentId) => {
    if (!window.confirm('Are you sure you want to delete this experiment?')) {
      return;
    }
    
    try {
      const response = await apiFetch("experiments/" + experimentId, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchExperiments();
      } else {
        const errorText = await response.text();
        setError(`Failed to delete experiment: ${errorText}`);
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    }
  };

  const renderExperimentForm = () => {
    return (
      <div className="experiment-form-container">
        <h3>Create New Experiment</h3>
        <form onSubmit={handleCreateExperiment} className="experiment-form">
          <div className="form-group">
            <label htmlFor="name">Experiment Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={newExperiment.name}
              onChange={handleInputChange}
              placeholder="Enter a descriptive name"
              required
              className={nameExists ? 'error' : ''}
            />
            {nameCheckLoading && <span className="loading-indicator">Checking...</span>}
            {nameExists && <span className="error-message">This name is already taken</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="model_1">Model 1:</label>
            <select
              id="model_1"
              name="model_1"
              value={newExperiment.model_1}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Model 1</option>
              {models.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="model_2">Model 2:</label>
            <select
              id="model_2"
              name="model_2"
              value={newExperiment.model_2}
              onChange={handleInputChange}
              disabled={!newExperiment.model_1}
              required
            >
              <option value="">Select Model 2</option>
              {models
                .filter(model => model !== newExperiment.model_1)
                .map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
            </select>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="create-button">Create Experiment</button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={() => setCreateMode(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderExperimentsList = () => {
    if (experiments.length === 0) {
      return (
        <div className="no-experiments">
          <p>You don't have any experiments yet.</p>
          <button 
            className="create-experiment-button"
            onClick={() => setCreateMode(true)}
          >
            Create Your First Experiment
          </button>
        </div>
      );
    }
    
    return (
      <>
        <div className="experiments-header">
          <h3>Your Experiments</h3>
          <button 
            className="create-experiment-button"
            onClick={() => setCreateMode(true)}
          >
            New Experiment
          </button>
        </div>
        
        <div className="experiments-list">
          {experiments.map(experiment => (
            <div key={experiment.id} className="compact-experiment-card">
              <h4>{experiment.name}</h4>
              <div className="compact-details">
                <p><strong>Models:</strong> {experiment.model_1} vs {experiment.model_2}</p>
                <p><strong>Progress:</strong> {experiment.completed} / {experiment.total} comparisons</p>
                <p><strong>Status:</strong> <span className={`status-${experiment.status}`}>{experiment.status}</span></p>
                <p><strong>Created:</strong> {new Date(experiment.created_at).toLocaleDateString()}</p>
              </div>
              <hr className="compact-divider" />
              <div className="compact-buttons">
                {experiment.status !== 'completed' && (
                  <button 
                    className="continue-button"
                    onClick={() => handleStartExperiment(experiment.id)}
                  >
                    {experiment.completed > 0 ? 'Continue' : 'Start'}
                  </button>
                )}
                <button 
                  className="results-button"
                  onClick={() => handleViewResults(experiment.id)}
                >
                  View Results
                </button>
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteExperiment(experiment.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderLoggedInHome = () => {
    if (loading) {
      return (
        <div className="loading">
          Loading experiments...
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>Try Again</button>
        </div>
      );
    }
    
    return (
      <div className="logged-in-content">
        {createMode ? renderExperimentForm() : renderExperimentsList()}
      </div>
    );
  };

  const renderPublicHome = () => {
    return (
      <section className="hero-section">
        <div className="hero-content">
          <h1>Image Arena</h1>
          <p className="subtitle">Compare AI-generated images with ease</p>
          <p className="description">
            A platform for conducting systematic comparisons between different image generation models
          </p>
          {!isLoggedIn && (
            <button onClick={onLogin} className="cta-button">
              Sign In to Get Started
            </button>
          )}
        </div>
      </section>
    );
  };

  const renderErrorModal = () => {
    if (!showErrorModal) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Cannot Create Experiment</h3>
            <button className="close-button" onClick={() => setShowErrorModal(false)}>×</button>
          </div>
          <div className="modal-body">
            <p>{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home-container">
      {isLoggedIn ? renderLoggedInHome() : renderPublicHome()}
      {renderErrorModal()}
    </div>
  );
}

export default Home; 